import { type GameState, type Character, createCharacter, createGameState } from './state.js';
import { applyEffects } from './effects.js';
import { type ActionId, ACTIONS } from '../domain/action.js';
import {
  type LocationId,
  LOCATIONS,
  travelHours,
  CORE_WORK_START,
  CORE_WORK_END,
  CORE_WORK_HOURS,
} from '../domain/location.js';
import { type JobId, JOBS, JOB_LADDER, jobLadderIndex, daysRequiredFor } from '../domain/job.js';
import { isAlive, educationLevel, HAPPINESS_DECAY_PER_HOUR } from '../domain/stats.js';
import { isGameOver, isOpenAt, hour, day, advance, type Clock } from '../domain/clock.js';

export type ErrorCode =
  | 'dead'
  | 'gameOver'
  | 'wrongLocation'
  | 'closed'
  | 'noJob'
  | 'noFood'
  | 'noTargetJob'
  | 'mustApplyNext'
  | 'lowEducation'
  | 'daysRequired'
  | 'sameLocation'
  | 'alreadyHasSuit'
  | 'noSuit'
  | 'noMoney';

export type ErrorParams = Readonly<Record<string, string | number>>;

export type Result<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly code: ErrorCode;
      readonly params?: ErrorParams;
    };

export type PerformOptions = {
  readonly targetJob?: JobId;
};

export const FULL_WORKDAY_HOURS = CORE_WORK_HOURS;

function demote(jobId: JobId | null): JobId | null {
  const idx = jobLadderIndex(jobId);
  if (idx <= 0) return null;
  return JOB_LADDER[idx - 1]!;
}

function isWorkday(d: number): boolean {
  return d % 7 < 5;
}

function applyDailyRollover(
  prevClock: Clock,
  nextClock: Clock,
  character: Character,
): Character {
  const startDay = day(prevClock);
  const endDay = day(nextClock);
  if (startDay === endDay) return character;

  let jobId = character.jobId;
  let warnings = character.warnings;
  let fullDays = character.fullWorkdaysInRole;

  for (let d = startDay; d < endDay; d++) {
    if (!isWorkday(d) || jobId === null) continue;
    const hoursThatDay = d === startDay ? character.hoursWorkedToday : 0;
    if (hoursThatDay >= FULL_WORKDAY_HOURS) {
      fullDays += 1;
      warnings = 0;
    } else if (warnings === 0) {
      warnings = 1;
    } else {
      jobId = demote(jobId);
      fullDays = 0;
      warnings = 0;
    }
  }

  return createCharacter({
    stats: character.stats,
    location: character.location,
    jobId,
    lastWorkDay: character.lastWorkDay,
    hoursWorkedToday: 0,
    fullWorkdaysInRole: fullDays,
    warnings,
    hasSuit: character.hasSuit,
  });
}

function fail(code: ErrorCode, params?: ErrorParams): Result<GameState> {
  return params ? { ok: false, code, params } : { ok: false, code };
}

function ok(value: GameState): Result<GameState> {
  return { ok: true, value };
}

export function perform(
  state: GameState,
  actionId: ActionId,
  opts: PerformOptions = {},
): Result<GameState> {
  if (!isAlive(state.character.stats)) return fail('dead');
  if (isGameOver(state.clock)) return fail('gameOver');

  const action = ACTIONS[actionId];
  if (action.locationId !== state.character.location) {
    return fail('wrongLocation', { location: action.locationId });
  }

  const loc = LOCATIONS[action.locationId];
  if (!isOpenAt(loc.openHour, loc.closeHour, hour(state.clock))) {
    return fail('closed', { location: action.locationId });
  }

  if (action.requirements?.requiresJob && state.character.jobId === null) {
    return fail('noJob');
  }

  if (
    action.requirements?.minFoodAtHome !== undefined &&
    state.character.stats.foodAtHome < action.requirements.minFoodAtHome
  ) {
    return fail('noFood');
  }

  if (action.requirements?.requiresNoSuit && state.character.hasSuit) {
    return fail('alreadyHasSuit');
  }

  const cost = action.effects.money ?? 0;
  if (cost < 0 && state.character.stats.money + cost < 0) {
    return fail('noMoney');
  }

  if (actionId === 'candidatar-se') {
    if (!opts.targetJob) return fail('noTargetJob');
    const target = JOBS[opts.targetJob];
    const targetIdx = jobLadderIndex(opts.targetJob);
    const currentIdx = jobLadderIndex(state.character.jobId);
    if (targetIdx !== currentIdx + 1) {
      return fail('mustApplyNext');
    }
    const currentLevel = educationLevel(state.character.stats.education);
    if (currentLevel < target.minEducation) {
      return fail('lowEducation', { job: opts.targetJob });
    }
    if (target.requiresSuit && !state.character.hasSuit) {
      return fail('noSuit', { job: opts.targetJob });
    }
    const daysRequired = daysRequiredFor(opts.targetJob);
    if (state.character.fullWorkdaysInRole < daysRequired) {
      return fail('daysRequired', {
        needed: daysRequired,
        current: state.character.fullWorkdaysInRole,
      });
    }
  }

  let nextStats = applyEffects(state.character.stats, action.effects);
  if (actionId === 'trabalhar' && state.character.jobId !== null) {
    const wage = JOBS[state.character.jobId].hourlyWage * action.duration;
    nextStats = applyEffects(nextStats, { money: wage });
  }
  nextStats = applyEffects(nextStats, { happiness: -HAPPINESS_DECAY_PER_HOUR * action.duration });

  const isPromotion = actionId === 'candidatar-se' && opts.targetJob !== undefined;
  const nextJobId = isPromotion ? opts.targetJob! : state.character.jobId;
  const nextLastWorkDay =
    actionId === 'trabalhar' ? day(state.clock) : state.character.lastWorkDay;
  const startHour = hour(state.clock);
  const isCoreHour =
    actionId === 'trabalhar' && startHour >= CORE_WORK_START && startHour < CORE_WORK_END;
  const hoursIncrement = isCoreHour ? action.duration : 0;
  const nextHoursWorkedToday = state.character.hoursWorkedToday + hoursIncrement;
  const nextFullWorkdays = isPromotion ? 0 : state.character.fullWorkdaysInRole;
  const nextWarnings = isPromotion ? 0 : state.character.warnings;

  const nextHasSuit = actionId === 'comprar-fato' ? true : state.character.hasSuit;
  let nextCharacter = createCharacter({
    stats: nextStats,
    location: state.character.location,
    jobId: nextJobId,
    lastWorkDay: nextLastWorkDay,
    hoursWorkedToday: nextHoursWorkedToday,
    fullWorkdaysInRole: nextFullWorkdays,
    warnings: nextWarnings,
    hasSuit: nextHasSuit,
  });
  const nextClock = advance(state.clock, action.duration);
  nextCharacter = applyDailyRollover(state.clock, nextClock, nextCharacter);
  return ok(createGameState({ character: nextCharacter, clock: nextClock }));
}

export function travel(state: GameState, destination: LocationId): Result<GameState> {
  if (!isAlive(state.character.stats)) return fail('dead');
  if (isGameOver(state.clock)) return fail('gameOver');
  if (state.character.location === destination) return fail('sameLocation');

  const hours = travelHours(state.character.location, destination);
  const decayedStats = applyEffects(state.character.stats, {
    happiness: -HAPPINESS_DECAY_PER_HOUR * hours,
  });
  let nextCharacter = createCharacter({
    stats: decayedStats,
    location: destination,
    jobId: state.character.jobId,
    lastWorkDay: state.character.lastWorkDay,
    hoursWorkedToday: state.character.hoursWorkedToday,
    fullWorkdaysInRole: state.character.fullWorkdaysInRole,
    warnings: state.character.warnings,
    hasSuit: state.character.hasSuit,
  });
  const nextClock = advance(state.clock, hours);
  nextCharacter = applyDailyRollover(state.clock, nextClock, nextCharacter);
  return ok(createGameState({ character: nextCharacter, clock: nextClock }));
}
